/**
 * Canonical Capability Taxonomy v1
 * 
 * 38 capability keys across 9 clusters.
 * Single source of truth for capability types used in training artifact mapping.
 */

export const CAPABILITY_KEYS = [
  // Diagnostic Thinking
  'diag_strategy',
  'diag_pattern_recognition',
  'diag_information_first',
  'diag_case_documentation',
  'diag_comeback_prevention',
  // Testing & Measurement
  'test_electrical_fundamentals',
  'test_oscilloscope_usage',
  'test_scan_tool_mastery',
  'test_network_diagnostics',
  'test_nvh_and_noise',
  // Systems Skill
  'systems_engine_performance',
  'systems_adas_calibration',
  'systems_hybrid_ev_safety',
  'systems_hvac_diagnostics',
  'systems_transmission_driveability',
  // Workflow & Efficiency
  'workflow_time_management',
  'workflow_shop_communication',
  'workflow_tooling_and_setup',
  'workflow_parts_delay_management',
  // Advisor — Communication & Selling
  'advisor_storytelling',
  'advisor_objection_handling',
  'advisor_expectation_setting',
  'advisor_deescalation',
  'advisor_follow_up_systems',
  // Advisor — Quality Workflow
  'advisor_dvi_quality',
  'advisor_repair_plan_building',
  'advisor_comeback_intake',
  // Manager/Owner — Shop Ops
  'ops_capacity_planning',
  'ops_constraint_management',
  'ops_process_design',
  'ops_kpi_literacy',
  'ops_quality_culture',
  // Manager/Owner — Leadership
  'leadership_coaching',
  'leadership_fairness_systems',
  'leadership_change_management',
  // Manager/Owner — Business
  'business_pricing_labor_strategy',
  'business_marketing_retention',
  'business_hiring_onboarding',
] as const;

export type CapabilityKey = typeof CAPABILITY_KEYS[number];

export type CapabilityCluster =
  | 'diagnostic_thinking'
  | 'testing_measurement'
  | 'systems_skill'
  | 'workflow_efficiency'
  | 'advisor_comm_selling'
  | 'advisor_quality_workflow'
  | 'ops_shop_operations'
  | 'ops_leadership'
  | 'ops_business';

export type RoleTarget = 'technician' | 'advisor' | 'manager' | 'owner';

export const CLUSTER_LABELS: Record<CapabilityCluster, string> = {
  diagnostic_thinking: 'Diagnostic Thinking',
  testing_measurement: 'Testing & Measurement',
  systems_skill: 'Systems Skill',
  workflow_efficiency: 'Workflow & Efficiency',
  advisor_comm_selling: 'Communication & Selling',
  advisor_quality_workflow: 'Inspection & Quality Workflow',
  ops_shop_operations: 'Shop Operations',
  ops_leadership: 'Leadership',
  ops_business: 'Business',
};

export const ROLE_TARGET_LABELS: Record<RoleTarget, string> = {
  technician: 'Tech',
  advisor: 'Advisor',
  manager: 'Manager',
  owner: 'Owner',
};

export interface TrainingCapability {
  key: CapabilityKey;
  label: string;
  cluster: CapabilityCluster;
  role_targets: RoleTarget[];
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ArtifactCapabilityMapRow {
  id: string;
  artifact_id: string;
  capability_key: CapabilityKey;
  weight: number;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

/**
 * Group capabilities by cluster for UI rendering
 */
export function groupCapabilitiesByCluster(
  capabilities: TrainingCapability[]
): Record<CapabilityCluster, TrainingCapability[]> {
  const groups = {} as Record<CapabilityCluster, TrainingCapability[]>;
  for (const cap of capabilities) {
    const cluster = cap.cluster as CapabilityCluster;
    if (!groups[cluster]) groups[cluster] = [];
    groups[cluster].push(cap);
  }
  // Sort within each cluster
  for (const cluster of Object.keys(groups) as CapabilityCluster[]) {
    groups[cluster].sort((a, b) => a.sort_order - b.sort_order);
  }
  return groups;
}

/**
 * Format cluster key to human-readable label
 */
export function formatClusterLabel(cluster: CapabilityCluster): string {
  return CLUSTER_LABELS[cluster] || cluster;
}

/**
 * Type guard for CapabilityKey
 */
export function validateCapabilityKey(key: string): key is CapabilityKey {
  return (CAPABILITY_KEYS as readonly string[]).includes(key);
}

/**
 * Clamp weight to valid range [0.1, 3.0]
 */
export function clampWeight(w: number): number {
  return Math.round(Math.max(0.1, Math.min(3.0, w)) * 10) / 10;
}
